/**
 * AI Scribe V2 — LIVE TRANSCRIPT column
 *
 * Auto-scrolls to the newest bubble and exposes the pause/resume listening
 * control in its header.
 *
 * @author [Rajkumar Rathod]
 */

import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { Box, Button } from "@mui/material";
import { useEffect } from "react";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import { SCRIBE_LIVE_TRANSCRIPT_LABELS, SCRIBE_V2_COLORS } from "../constants";
import { ScribeStatus, ScribeTranscriptLine } from "../types";
import { useCopyTranscript } from "../use-copy-transcript";
import { useStickToBottom } from "../use-stick-to-bottom";
import CopyTranscriptButton from "./CopyTranscriptButton";
import ListeningIndicator from "./ListeningIndicator";
import ScribePanelHeader from "./ScribePanelHeader";
import TranscriptThread from "./TranscriptThread";

interface LiveTranscriptPanelProps {
  transcript: ScribeTranscriptLine[];
  status: ScribeStatus;
  onTogglePause: () => void;
}

const EMPTY_COPY: Partial<Record<ScribeStatus, string>> = {
  IDLE: "Start the session to see the live transcript.",
  LISTENING: "Listening… the transcript will appear as the visit is spoken.",
  PAUSED: "Listening is paused.",
  COMPLETED: "No transcript was captured for this session.",
  ERROR: "Microphone unavailable — check browser permissions and try again.",
};

const LiveTranscriptPanel = ({
  transcript,
  status,
  onTogglePause,
}: LiveTranscriptPanelProps) => {
  const { scrollRef, isPinned, onScroll, scrollToBottom, stickIfPinned } =
    useStickToBottom();
  const isPaused = status === "PAUSED";
  const canTogglePause = status === "LISTENING" || status === "PAUSED";
  const showJumpToLatest = transcript.length > 0 && !isPinned;
  const { copyTranscript, canCopy } = useCopyTranscript(transcript);

  useEffect(() => {
    if (!transcript.length) return;
    stickIfPinned();
  }, [transcript.length, stickIfPinned]);

  return (
    <Flex flexDirection="column" height="100%" sx={{ minWidth: 0, flex: 1 }}>
      <ScribePanelHeader
        icon={<ChatBubbleOutlineOutlinedIcon />}
        label="Live Transcript"
        action={
          <Flex alignItems="center" gap={1.5}>
            <CopyTranscriptButton onCopy={copyTranscript} disabled={!canCopy} />
            {canTogglePause && (
              <Button
                size="small"
                disableRipple
                onClick={onTogglePause}
                startIcon={
                  isPaused ? (
                    <PlayCircleOutlinedIcon />
                  ) : (
                    <PauseCircleOutlinedIcon />
                  )
                }
                sx={{
                  minWidth: "auto",
                  p: 0,
                  color: SCRIBE_V2_COLORS.panelLabel,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b6),
                  fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  "& .MuiButton-startIcon svg": {
                    fontSize: px(PRIMITIVE.spacing[16]),
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: SCRIBE_V2_COLORS.textPrimary,
                  },
                }}
              >
                {isPaused ? "Resume Listening" : "Pause Listening"}
              </Button>
            )}
          </Flex>
        }
      />

      <Box
        sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}
      >
        <Box
          ref={scrollRef}
          onScroll={onScroll}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: px(PRIMITIVE.spacing[12]),
            flex: 1,
            overflowY: "auto",
          }}
        >
          {transcript.length === 0 ? (
            <Flex
              flex={1}
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              px={2}
            >
              <Text
                sx={{
                  color: SCRIBE_V2_COLORS.textSecondary,
                  fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
                }}
              >
                {EMPTY_COPY[status] ?? EMPTY_COPY.IDLE}
              </Text>
            </Flex>
          ) : (
            <TranscriptThread
              lines={transcript}
              trailing={
                status === "LISTENING" ? <ListeningIndicator /> : undefined
              }
            />
          )}
        </Box>

        {showJumpToLatest && (
          <Button
            size="small"
            disableRipple
            onClick={() => scrollToBottom()}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              position: "absolute",
              bottom: px(PRIMITIVE.spacing[12]),
              left: "50%",
              transform: "translateX(-50%)",
              textTransform: "none",
              px: px(PRIMITIVE.spacing[12]),
              color: SCRIBE_V2_COLORS.textPrimary,
              backgroundColor: SCRIBE_V2_COLORS.windowBg,
              border: `1px solid ${SCRIBE_V2_COLORS.noteCardBorder}`,
              borderRadius: "999px",
              fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
              fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
              boxShadow: `0 ${px(PRIMITIVE.shadow.offset[400])} ${px(
                PRIMITIVE.shadow.offset[800]
              )} ${PRIMITIVE.shadow.color.medium}`,
              "&:hover": {
                backgroundColor: SCRIBE_V2_COLORS.windowBg,
              },
            }}
          >
            {SCRIBE_LIVE_TRANSCRIPT_LABELS.JUMP_TO_LATEST}
          </Button>
        )}
      </Box>
    </Flex>
  );
};

export default LiveTranscriptPanel;
